import { useMutation, useQuery } from '@tanstack/react-query'
import services from './auth-services'
import { AppointmentConfirmationParams } from './types/en-us'
import { MinorAccessRequestData } from './components/MinorAccessRequestModal/types'
import { getCache } from '@/utils/cache'

export const useSend2FACode = (enabled: boolean) => {
  return useQuery({
    queryKey: ['send-2fa'],
    queryFn: async () => {
      return await services.sendTwoFactorAuthCode()
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled,
  })
}

export const useValidate2FACode = (code: string) => {
  return useQuery({
    queryKey: ['validate-2fa'],
    queryFn: async () => {
      return await services.validateTwoFactorAuthCode(code)
    },
    refetchOnWindowFocus: false,
    retry: false,
    enabled: false,
  })
}

export const useConfirmAppointment = (data: AppointmentConfirmationParams) => {
  return useQuery({
    queryKey: ['confirm-appointment', data],
    queryFn: async () => await services.confirmAppointment(data),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!(data?.attendanceToken && data?.userToken),
  })
}

export const useGetLegalGuardianRelationship = () => {
  const token = getCache<string>('token')
  return useQuery({
    queryKey: ['legal-guardian-relationship-enum'],
    queryFn: async () => services.getLegalGuardianRelationship(token),
    enabled: !!token,
    refetchOnWindowFocus: false,
    retry: false,
  })
}

export const useCheckLegalGuardianData = (idPatient: number) => {
  return useQuery({
    queryKey: ['legal-guardian-data', idPatient],
    queryFn: () => services.checkLegalGuardianData(idPatient),
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !!idPatient,
  })
}

export const useSendMinorAccessRequest = () => {
  return useMutation({
    mutationFn: async (data: MinorAccessRequestData) =>
      await services.sendMinorGuardianData(data),
  })
}

export const useCheckMinorsTermAcceptance = () => {
  return useMutation({
    mutationFn: async (token: string) =>
      await services.checkMinorsTermAcceptance(token),
  })
}

export const useCheckTermAcceptance = () => {
  return useMutation({
    mutationFn: async (token?: string) =>
      await services.checkTermsAcceptance(token),
  })
}

export const useAuthMagicLink = () => {
  return useMutation({
    mutationFn: async (token: string) => await services.authMagicLink(token),
    retry: false,
  })
}

export const useMagicLinkByAccount = () => {
  return useMutation({
    mutationFn: async (token: string) =>
      await services.magicLinkByAccount(token),
  })
}

export const useGetUsersProfilesFlowByAccount = () => {
  return useMutation({
    mutationFn: async (token: string) =>
      await services.getUsersProfilesFlowByAccount(token),
  })
}
